import React, { createRef, FC, useEffect, useRef } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';
import { CssBaseline, IconButton } from '@material-ui/core';
import { SnackbarProvider } from 'notistack';
import { Close } from '@material-ui/icons';
import {
  PATH_IB_DASHBOARD,
  PATH_PARTNERS_REPRESENTATIVES,
  PATH_COMMISSIONS_BY_CLIENT,
  PATH_CLIENTS_LIST,
  PATH_TRADING_SUMMARY,
  PATH_CLOSED_POSITIONS,
  PATH_MASTER_IB_COMMISSION,
  PATH_PARTNERS_HIERARCHY,
  PATH_NOTIFICATIONS,
  PATH_LOGIN_AS_PARTNER,
  PATH_LOGIN_AS_REPRESENTATIVE,
  PATH_SIGN_UP,
  PATH_OPEN_CLOSE_POSITIONS_SUMMARY,
  PATH_TRADING_ACTIVITIES_REPORT,
  PATH_ERROR_PAGE,
  extractLocaleFromPath,
  PATH_WELCOME,
  pathWithOrWithoutLocale,
  PATH_REGISTRATION_LINKS,
  PATH_FORGOT_PASSWORD,
  PATH_CASHBACK,
  PATH_IB_CLIENTS_CASHBACK,
  PATH_IB_LINK_STATISTICS,
} from './utils';
import {
  WelcomePage,
  IBDashboard,
  CommissionsByClient,
  ClientsList,
  TradingSummary,
  ClosedPositions,
  MasterIbCommission,
  PartnersHierarchy,
  Representatives,
  NotificationsPage,
  LoginAsPartner,
  LoginAsRepresentative,
  RepresentativeSignUp,
  OpenClosedPositionsSummary,
  TradingActivitiesReport,
  ErrorPage,
  RestorePassword,
  RegistrationLinks,
  Cashback,
  IBClientsCashback,
  IBLinkStatisticsReport,
} from './pages';
import { AuthRoute, Spinner } from './components';
import { GlobalStyles } from './styles';
import { useStyles } from './appRouteStyles';
import { initAmplitude, initCrossDomainAmplitude } from './services/amplitudeService';
import { useAppData } from './providers';
import { connect, disconnect } from './services/centrifugeService';

export const AppRoute: FC = () => {
  const classes = useStyles();
  const notistackRef = createRef<any>();
  const location = useLocation();
  const lang = extractLocaleFromPath(location.pathname);
  const { isUserLoggedIn, authToken } = useAppData();
  const tokenRef = useRef(authToken);

  useEffect(() => {
    tokenRef.current = authToken;
  }, [authToken]);

  useEffect(() => {
    if (isUserLoggedIn) {
      connect();
    } else {
      disconnect();
    }
  }, [isUserLoggedIn]);

  useEffect(() => {
    initAmplitude();
    initCrossDomainAmplitude();
  }, []);

  const onClickDismiss = (key: any) => () => {
    notistackRef.current.closeSnackbar(key);
  };

  return (
    <SnackbarProvider
      maxSnack={4}
      hideIconVariant
      classes={{
        variantSuccess: classes.success,
        variantError: classes.error,
        variantWarning: classes.warning,
        variantInfo: classes.info,
      }}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      ref={notistackRef}
      action={(key) => (
        <IconButton className={classes.btn} onClick={onClickDismiss(key)}>
          <Close />
        </IconButton>
      )}
    >
      <CssBaseline />
      <GlobalStyles />
      <Spinner />
      <Route exact path={pathWithOrWithoutLocale(PATH_WELCOME, lang)} component={WelcomePage} />
      <Route exact path={PATH_LOGIN_AS_PARTNER} component={LoginAsPartner} />
      <Route exact path={pathWithOrWithoutLocale(PATH_LOGIN_AS_REPRESENTATIVE, lang)} component={LoginAsRepresentative} />
      <Route exact path={pathWithOrWithoutLocale(PATH_SIGN_UP, lang)} component={RepresentativeSignUp} />
      <Route exact path={pathWithOrWithoutLocale(PATH_FORGOT_PASSWORD, lang)} component={RestorePassword} />
      <Route exact path={pathWithOrWithoutLocale(PATH_ERROR_PAGE, lang)} component={ErrorPage} />
      <Switch>
        <AuthRoute path={PATH_IB_DASHBOARD} Component={IBDashboard} />
        <AuthRoute path={PATH_PARTNERS_HIERARCHY} Component={PartnersHierarchy} />
        <AuthRoute path={PATH_PARTNERS_REPRESENTATIVES} Component={Representatives} />
        <AuthRoute path={PATH_COMMISSIONS_BY_CLIENT} Component={CommissionsByClient} />
        <AuthRoute path={PATH_TRADING_SUMMARY} Component={TradingSummary} />
        <AuthRoute path={PATH_CLIENTS_LIST} Component={ClientsList} />
        {/* <AuthRoute path={PATH_ALL_CLIENTS_AND_TRADING} Component={AllClientsAndTrading} /> */}
        <AuthRoute path={PATH_CLOSED_POSITIONS} Component={ClosedPositions} />
        <AuthRoute path={PATH_MASTER_IB_COMMISSION} Component={MasterIbCommission} />
        <AuthRoute path={PATH_NOTIFICATIONS} Component={NotificationsPage} />
        {/* <AuthRoute path={PATH_TRANSACTIONS_REPORT} Component={TransactionsReport} />  */}
        <AuthRoute path={PATH_OPEN_CLOSE_POSITIONS_SUMMARY} Component={OpenClosedPositionsSummary} />
        <AuthRoute path={PATH_TRADING_ACTIVITIES_REPORT} Component={TradingActivitiesReport} />
        <AuthRoute path={PATH_REGISTRATION_LINKS} Component={RegistrationLinks} />
        <AuthRoute path={PATH_CASHBACK} Component={Cashback} />
        <AuthRoute path={PATH_IB_CLIENTS_CASHBACK} Component={IBClientsCashback} />
        <AuthRoute path={PATH_IB_LINK_STATISTICS} Component={IBLinkStatisticsReport} />
      </Switch>
    </SnackbarProvider>
  );
};
